import { useEffect, useRef } from "react";
import { api, ApiError } from "./lib/api";
import { advanceAggregateClock, aggregatePayload, claimAggregateView, createAggregateVisit, drainAggregateClock, validAggregateListing, type AggregateDocument, type AggregatePage, type AggregatePayload, type AggregateVisit } from "./data/aggregate-analytics";

// These booleans/route labels live only in the current document's memory.
// No local/session storage, cookies, random IDs or browser fingerprinting.
const documentCounter: AggregateDocument = { accessClaimed: false };
const pendingRequests = new Set<AbortController>();
const timeNow = () => performance.now();
const isVisible = () => document.visibilityState === "visible";
const isFocused = () => document.hasFocus();

export function useVisitAnalytics({ page, listingId, enabled, userIsAdmin = false }: { page: AggregatePage; listingId?: string; enabled: boolean; userIsAdmin?: boolean }): void {
  const visitRef = useRef<AggregateVisit | null>(null);
  useEffect(() => {
    if (!enabled || userIsAdmin || (listingId !== undefined && !validAggregateListing(listingId))) {
      for (const controller of pendingRequests) controller.abort();
      return;
    }
    const key = `${page}:${listingId || ""}`;
    if (!visitRef.current || visitRef.current.key !== key) visitRef.current = createAggregateVisit(page, listingId, timeNow(), isVisible(), isFocused());
    const current = visitRef.current;
    current.clock = { ...current.clock, lastAt: timeNow(), active: isVisible() && isFocused() };
    let stopped = false;

    const send = (payload: AggregatePayload, final = false) => {
      if (!current.available || (stopped && !final)) return;
      const controller = new AbortController();
      pendingRequests.add(controller);
      const timeout = window.setTimeout(() => controller.abort(), 5000);
      void api.request<{ accepted: boolean; available: boolean }>("/analytics/aggregate", { method: "POST", body: JSON.stringify(payload), signal: controller.signal, keepalive: final })
        .then(result => { if (result.available === false || result.accepted === false) current.available = false; })
        .catch(error => { if (error instanceof ApiError && [400, 401, 403, 404, 422].includes(error.status)) current.available = false; })
        .finally(() => { window.clearTimeout(timeout); pendingRequests.delete(controller); });
      // Failed increments are never retried: there is no per-visitor event ID.
    };
    const updateClock = () => { current.clock = advanceAggregateClock(current.clock, timeNow(), isVisible(), isFocused()); };
    const beginVisiblePage = () => {
      const payload = claimAggregateView(documentCounter, current, isVisible(), isFocused());
      if (!payload) return;
      current.clock = { lastAt: timeNow(), active: isVisible() && isFocused(), elapsedMs: 0 };
      send(payload);
    };
    const flushActiveTime = (final = false) => {
      if (!current.started || !current.available) return;
      const drained = drainAggregateClock(current.clock);
      current.clock = drained.clock;
      if (drained.seconds === 0) return;
      const payload = aggregatePayload("active", page, drained.seconds);
      if (payload) send(payload, final);
    };
    const activityChanged = () => { updateClock(); beginVisiblePage(); if (!isVisible() || !isFocused()) flushActiveTime(); };
    const heartbeat = window.setInterval(() => { updateClock(); beginVisiblePage(); flushActiveTime(); }, 15000);
    const pageHiding = () => { updateClock(); flushActiveTime(true); };
    document.addEventListener("visibilitychange", activityChanged);
    window.addEventListener("focus", activityChanged); window.addEventListener("blur", activityChanged);
    window.addEventListener("pagehide", pageHiding);
    beginVisiblePage();
    return () => {
      updateClock(); stopped = true;
      window.clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", activityChanged);
      window.removeEventListener("focus", activityChanged); window.removeEventListener("blur", activityChanged);
      window.removeEventListener("pagehide", pageHiding);
      flushActiveTime(true);
    };
  }, [enabled, userIsAdmin, page, listingId]);
}
